import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    OnChanges,
    SimpleChange,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import _ from 'lodash';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzIconDirective, NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent, NzInputGroupWhitSuffixOrPrefixDirective } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { ScreenLoggedService } from 'src/app/core/services/screenLogged.service';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { ConfigurationService } from 'src/app/private/services/configuration.service';
import { AuthService } from 'src/app/public/services/auth.service';
import { ScreenDto } from 'src/app/shared/dto/screen.dto';
import { isEmail } from 'src/app/shared/utils/utils';
import { environment } from 'src/environments/environment';
import packageJson from '../../../../../package.json';
import { PermissionService } from 'src/app/core/services/permission.service';
import { AuthMethod } from 'src/app/shared/enums/auth-method.enum';
import { IconDefinition } from '@ant-design/icons-angular';
import { EyeInvisibleOutline } from '@ant-design/icons-angular/icons';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { UserService } from 'src/app/private/services/user.service';
import { CONFIGURATIONS } from 'src/app/core/constants/storage';
import { LocalStorageService } from 'src/app/core/services/storage.service';

const icons: IconDefinition[] = [
    EyeInvisibleOutline
];

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    standalone: true,
    imports: [
        NzInputDirective,
        ReactiveFormsModule,
        FormsModule,
        ɵNzTransitionPatchDirective,
        NzInputGroupComponent,
        NzInputGroupWhitSuffixOrPrefixDirective,
        NzIconDirective,
        NzButtonComponent,
        NzWaveDirective,
        NzMessageModule,
        NzIconModule
    ],
})
export class LoginFormComponent implements OnChanges, AfterViewInit {
    loginScreen: ScreenDto | null = null;
    screenToBeLogged: Screen | null = null;
    user: string = '';
    codeSecurity!: number;
    password: string = '';
    passwordVisible = false;
    environment: any;
    version: any = packageJson.version;
    helper: JwtHelperService;
    showLoginButton: boolean = true;
    showLoginInput: boolean = true;

    @ViewChild('userInput') userInput!: ElementRef;

    
    private readonly authService = inject(AuthService);
    private readonly userService = inject(UserService);
    private readonly screenLoggedService = inject(ScreenLoggedService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly toast = inject(NzMessageService);
    private readonly router = inject(Router);
    private readonly configurationService = inject(ConfigurationService);
    private readonly permissionService = inject(PermissionService);
    private readonly iconService = inject(NzIconService);
    private readonly localStorageService = inject(LocalStorageService);

    constructor() {
        this.helper = new JwtHelperService();
        this.environment = _.cloneDeep(environment);
        this.iconService.addIcon(...icons);
    }

    ngOnChanges(changes: SimpleChanges) {
        const loginScreen: SimpleChange = changes['loginScreen'];

        if (
            loginScreen != undefined &&
            loginScreen.previousValue != loginScreen.currentValue
        ) {
            this.screenToBeLogged = _.cloneDeep(loginScreen.currentValue);
        }
    }

    ngAfterViewInit() {
        this.userInput.nativeElement.focus();
    }

    async login(): Promise<void> {
        try {
            if (this.user === '' || this.password === '') {
                this.toast.error('Porfavor, complete todos los campos');

                return;
            }

            let authToken = null;

            if (this.loginScreen) {
                authToken = await this.authService.login(
                    isEmail(this.user) ? AuthMethod.EMAIL : AuthMethod.USER,
                    this.user,
                    { password: this.password, screenId: this.loginScreen.id }
                );
            } else {
                authToken = await this.authService.login(
                    isEmail(this.user) ? AuthMethod.EMAIL : AuthMethod.USER,
                    this.user,
                    { password: this.password }
                );
            }

            if (!authToken.accessToken || authToken.accessToken === '') {
                this.showLoginButton = false;
                this.showLoginInput = false;
            } else {
                const dataToken = this.helper.decodeToken(
                    authToken.accessToken
                );
                this.userLoggedService.setToken(
                    authToken.accessToken
                );

                const user = await this.userService.getById(dataToken.user.id) as UserDto;
                this.userLoggedService.setUserLogged(
                    user
                );

                if(authToken.screen){
                    this.screenLoggedService.setScreen(
                        authToken.screen
                    );
                }

                const configurations = await this.configurationService.getConfigurations().catch((error) => {
                    console.error(error);
                    this.toast.error('No se han podido cargar las configuraciones');
                });

                this.localStorageService.setItem(CONFIGURATIONS, configurations);

                this.permissionService.loadPermissions();

                this.router.navigate(['/production-orders']);
            }
        } catch (error: any) {
            this.toast.error(error.error.error);

            return;
        }
    }

    async registerUserSupport(): Promise<void> {
        try {
            const isValidUser = await this.authService.registerUser(
                isEmail(this.user) ? AuthMethod.EMAIL : AuthMethod.USER,
                this.user,
                { password: this.password }
            );

            if (isValidUser) {
                this.toast.success("Operario registrado en esta pantalla");
            } else {
                this.toast.error("RFID no autorizado");
            }
        } catch (error) {
            this.toast.error("Error al registrar el operario");
        }
    }

    async verifyCode(): Promise<void> {
        if (+this.codeSecurity == 0) {
            this.toast.error('Porfavor, complete el código de seguridad');

            return;
        }

        try {
            let authToken = null;

            if (this.loginScreen) {
                authToken = await this.authService.sendCode(
                    isEmail(this.user) ? AuthMethod.EMAIL : AuthMethod.USER,
                    this.user,
                    +this.codeSecurity,
                    { password: this.password, screenId: this.loginScreen.id }
                );
            } else {
                authToken = await this.authService.sendCode(
                    isEmail(this.user) ? AuthMethod.EMAIL : AuthMethod.USER,
                    this.user,
                    +this.codeSecurity,
                    { password: this.password }
                );
            }

            const dataToken = this.helper.decodeToken(authToken.accessToken);

            this.userLoggedService.setToken(
                authToken.accessToken
            );

            const user = await this.userService.getById(dataToken.user.id) as UserDto;
            this.userLoggedService.setUserLogged(
                user
            );

            this.screenLoggedService.setScreen(
                this.loginScreen ? this.loginScreen : null,
            );

            this.permissionService.loadPermissions();

            this.router.navigate(['/']);
        } catch (error: any) {
            this.toast.error('El codigo no es correcto');

            return;
        }
    }
}
